// Importing React and necessary hooks for state management and lifecycle methods
import React, { useState, useEffect } from 'react';
import { debug } from '../utils/debugUtils';

// Importing useNavigate from react-router-dom to handle navigation after login
import { useNavigate, useLocation } from 'react-router-dom';

// Importing shadcn/ui components
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

// Importing Lucide React icons
import { Loader2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext'

function Login() {
  // State to hold form inputs and error messages
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();

  useEffect(()=>{
    const checkAuthentication = async () =>{
      const isAuth = await checkAuth();
      if (isAuth){
        // Only redirect to dashboard if user is on the login page
        // Don't redirect if they're on a public route like /quote-response
        if (location.pathname === '/') {
          navigate('/dashboard');
        }
      }
    };
    checkAuthentication();
  }, [navigate, checkAuth, location.pathname]);

  // Form validation function
  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid'; // Checks for valid email format
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters'; // Password length validation
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault(); // Prevents page reload
    const formErrors = validateForm(); // Validate form inputs
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors); // Display errors if validation fails
    } else {
      setErrors({});
    debug.component.login('Login attempted with:', { email, password: '***' }); // Hide password in logs
      // Send login request to backend here
    }
  };

  // Google OAuth login handler
  const handleGoogleLogin = () => {
    setIsLoading(true);
    // Use local API endpoint for development
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? 'https://test.api.printereum.com' 
      : 'http://localhost:3001';
    window.location.href = `${apiUrl}/auth/google`;
  };

  
  // Microsoft OAuth login handler
  const handleMicrosoftLogin = () => {
    setIsLoading(true);
    // Use local API endpoint for development
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? 'https://test.api.printereum.com' 
      : 'http://localhost:3001';
    window.location.href = `${apiUrl}/auth/microsoft`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Modern Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-indigo-50/30 to-purple-50/50"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(147,51,234,0.1),transparent_50%)]"></div>
      
      {/* Print Registration Marks - Full CMYK Setup */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top Left - Cyan */}
        <div className="absolute top-5 left-5 w-6 h-1 bg-cyan-400 opacity-60"></div>
        <div className="absolute top-5 left-5 w-1 h-6 bg-cyan-400 opacity-60"></div>
        
        {/* Top Right - Magenta */}
        <div className="absolute top-5 right-5 w-6 h-1 bg-pink-500 opacity-60"></div>
        <div className="absolute top-5 right-5 w-1 h-6 bg-pink-500 opacity-60"></div>
        
        {/* Bottom Left - Yellow */}
        <div className="absolute bottom-5 left-5 w-6 h-1 bg-yellow-400 opacity-60"></div>
        <div className="absolute bottom-5 left-5 w-1 h-6 bg-yellow-400 opacity-60"></div>
        
        {/* Bottom Right - Black */}
        <div className="absolute bottom-5 right-5 w-6 h-1 bg-gray-800 opacity-60"></div>
        <div className="absolute bottom-5 right-5 w-1 h-6 bg-gray-800 opacity-60"></div>

        {/* Additional Registration Marks for Print Accuracy */}
        {/* Center Top - Cross Mark */}
        <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-1 h-6 bg-gray-600 opacity-40"></div>
        <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-gray-600 opacity-40"></div>
        
        {/* Center Bottom - Cross Mark */}
        <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 w-1 h-6 bg-gray-600 opacity-40"></div>
        <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 w-6 h-1 bg-gray-600 opacity-40"></div>
        
        {/* Left Center - Cross Mark */}
        <div className="absolute top-1/2 transform -translate-y-1/2 left-5 w-6 h-1 bg-gray-600 opacity-40"></div>
        <div className="absolute top-1/2 transform -translate-y-1/2 left-5 w-1 h-6 bg-gray-600 opacity-40"></div>
        
        {/* Right Center - Cross Mark */}
        <div className="absolute top-1/2 transform -translate-y-1/2 right-5 w-6 h-1 bg-gray-600 opacity-40"></div>
        <div className="absolute top-1/2 transform -translate-y-1/2 right-5 w-1 h-6 bg-gray-600 opacity-40"></div>

        {/* Bleed Marks - Professional Print Standards */}
        {/* Top Bleed Marks */}
        <div className="absolute top-2 left-1/4 w-1 h-3 bg-red-500 opacity-70"></div>
        <div className="absolute top-2 left-3/4 w-1 h-3 bg-red-500 opacity-70"></div>
        <div className="absolute top-2 left-1/2 w-1 h-3 bg-red-500 opacity-70"></div>
        
        {/* Bottom Bleed Marks */}
        <div className="absolute bottom-2 left-1/4 w-1 h-3 bg-red-500 opacity-70"></div>
        <div className="absolute bottom-2 left-3/4 w-1 h-3 bg-red-500 opacity-70"></div>
        <div className="absolute bottom-2 left-1/2 w-1 h-3 bg-red-500 opacity-70"></div>
        
        {/* Left Bleed Marks */}
        <div className="absolute left-2 top-1/4 w-3 h-1 bg-red-500 opacity-70"></div>
        <div className="absolute left-2 top-3/4 w-3 h-1 bg-red-500 opacity-70"></div>
        <div className="absolute left-2 top-1/2 w-3 h-1 bg-red-500 opacity-70"></div>
        
        {/* Right Bleed Marks */}
        <div className="absolute right-2 top-1/4 w-3 h-1 bg-red-500 opacity-70"></div>
        <div className="absolute right-2 top-3/4 w-3 h-1 bg-red-500 opacity-70"></div>
        <div className="absolute right-2 top-1/2 w-3 h-1 bg-red-500 opacity-70"></div>

        {/* Cut Marks - Professional Print Standards */}
        {/* Corner Cut Marks */}
        <div className="absolute top-3 left-3 w-4 h-1 bg-black opacity-80"></div>
        <div className="absolute top-3 left-3 w-1 h-4 bg-black opacity-80"></div>
        
        <div className="absolute top-3 right-3 w-4 h-1 bg-black opacity-80"></div>
        <div className="absolute top-3 right-3 w-1 h-4 bg-black opacity-80"></div>
        
        <div className="absolute bottom-3 left-3 w-4 h-1 bg-black opacity-80"></div>
        <div className="absolute bottom-3 left-3 w-1 h-4 bg-black opacity-80"></div>
        
        <div className="absolute bottom-3 right-3 w-4 h-1 bg-black opacity-80"></div>
        <div className="absolute bottom-3 right-3 w-1 h-4 bg-black opacity-80"></div>

        {/* Print Information Labels */}
        <div className="absolute top-8 left-8 text-xs text-gray-600 opacity-60 font-mono">
          <div>BLEED: 0.125"</div>
          <div>CUT: 0.0625"</div>
        </div>
        
        <div className="absolute top-8 right-8 text-xs text-gray-600 opacity-60 font-mono text-right">
          <div>SAFE: 0.25"</div>
          <div>REG: 0.125"</div>
        </div>

        {/* Print Job Information */}
        <div className="absolute bottom-8 left-8 text-xs text-gray-600 opacity-60 font-mono">
          <div>JOB: LOGIN-001</div>
          <div>DATE: {new Date().toLocaleDateString('en-US', { timeZone: 'UTC' })}</div>
        </div>
        
        <div className="absolute bottom-8 right-8 text-xs text-gray-600 opacity-60 font-mono text-right">
          <div>PAPER: DIGITAL</div>
          <div>PROOF: APPROVED</div>
        </div>
      </div>

      {/* Modern CMYK Color Bar */}
      <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-48 h-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-white/20">
        <div className="flex h-full rounded-full overflow-hidden">
          <div className="w-1/4 h-full bg-gradient-to-r from-cyan-400 to-cyan-500"></div>
          <div className="w-1/4 h-full bg-gradient-to-r from-pink-500 to-pink-600"></div>
          <div className="w-1/4 h-full bg-gradient-to-r from-yellow-400 to-yellow-500"></div>
          <div className="w-1/4 h-full bg-gradient-to-r from-gray-800 to-gray-900"></div>
        </div>
      </div>

      {/* Modern Print Quality Control Bar */}
      <div className="absolute top-16 left-1/2 transform -translate-x-1/2 w-64 h-1 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-white/20">
        <div className="flex justify-between px-2 text-xs text-gray-500">
          <span>10%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      <div className="max-w-6xl w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden min-h-[600px]">
          {/* Left side - Branding */}
          <div className="hidden lg:flex bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative">
            {/* Modern pattern overlay */}
            <div className="absolute inset-0 opacity-10">
              <div className="w-full h-full" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundRepeat: 'repeat'
              }}></div>
            </div>
            
            {/* Print Registration Mark */}
            <div className="absolute top-5 right-5 text-yellow-400 text-xl font-bold">⊕</div>
            
            <div className="relative z-10 flex items-center justify-center p-12 text-center text-white w-full">
              <div className="flex flex-col items-center w-full max-w-lg px-4 mb-8 gap-y-2">
                  <img
                    src="/assets/logo no background.png"
                    alt="Printereum Logo"
                    className="w-32 h-32 rounded-full mb-2"
                  />
                  <h1
                    style={{
                      color: '#ed1d35',
                      fontWeight: 700,
                      fontSize: '3rem',
                      textAlign: 'center',
                      margin: 0,
                    }}
                  >
                    Printereum
                  </h1>
                  <p
                    className="text-lg text-gray-300"
                    style={{ margin: 0 }}
                  >
                    Professional Print Management Platform
                  </p>
                  <div className="w-full flex flex-col items-center gap-y-2 mt-4">
                    {/* Features list starts here */}
                    <div className="flex flex-col items-start">
                      <span className="flex items-center mb-1"><span className="w-3 h-3 rounded-full bg-cyan-400 mr-2"></span>Quote & Order Management</span>
                      <span className="flex items-center mb-1"><span className="w-3 h-3 rounded-full bg-pink-500 mr-2"></span>Production Tracking</span>
                      <span className="flex items-center mb-1"><span className="w-3 h-3 rounded-full bg-yellow-400 mr-2"></span>Customer Dashboard</span>
                      <span className="flex items-center"><span className="w-3 h-3 rounded-full bg-gray-800 mr-2"></span>Analytics & Reporting</span>
                    </div>
                    {/* Features list ends here */}
                  </div>
                </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className="bg-white/90 backdrop-blur-sm p-8 lg:p-12 flex items-center justify-center min-h-[600px]">
            <div className="w-full max-w-md">
              <div className="text-center mb-8">
                {/* Mobile logo */}
                <div className="lg:hidden mb-6">
                  <div className="w-20 h-20 mx-auto relative">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 via-pink-500 via-yellow-400 to-gray-800 p-1">
                      <div className="w-full h-full rounded-full bg-black flex items-center justify-center shadow-2xl">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center border-2 border-white/20">
                          <img
                            src="/assets/logo no background.png"
                            alt="Printereum"
                            className="w-14 h-14 rounded-full object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <h1
                    style={{
                      color: '#ed1d35',
                      fontWeight: 700,
                      fontSize: '2rem',
                      textAlign: 'center',
                      margin: 0,
                      marginTop: '1rem',
                      marginBottom: 0,
                    }}
                  >
                    Printereum
                  </h1>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
                <p className="text-gray-600">Sign in to your print management dashboard</p>
              </div>

              <Card className="relative border-0 shadow-xl bg-white/80 backdrop-blur-sm rounded-3xl">
                {/* Print Marks */}
                <img
                  src="/assets/input_cut_mark-removebg-preview.png"
                  alt="Cut Mark"
                  className="absolute top-3 left-3 w-6 h-6 opacity-100 pointer-events-none"
                  style={{ zIndex: 2, filter: 'brightness(0) invert(0)' }}
                />
                <img
                  src="/assets/input_fold_marks-removebg-preview.png"
                  alt="Fold Marks"
                  className="absolute bottom-3 right-3 w-10 h-4 opacity-100 pointer-events-none"
                  style={{ zIndex: 2, filter: 'brightness(0) invert(0)' }}
                />
                {/* CMYK Bar Accent */}
                <img
                  src="/assets/color_bar_with_spot_and_fold-removebg-preview.png"
                  alt="CMYK Bar"
                  className="w-full h-3 object-cover"
                  style={{ minHeight: 12, maxHeight: 16 }}
                />
                <CardContent className="p-8">
                  {/* OAuth Login Buttons */}
                  <div className="space-y-4">
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full h-14 border-2 border-gray-200 bg-white/90 backdrop-blur-sm hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 ease-out font-medium text-base"
                      onClick={handleGoogleLogin}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center space-x-3">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Signing in...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-3">
                          <svg className="w-6 h-6" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          <span>Continue with Google</span>
                        </div>
                      )}
                    </Button>

                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full h-14 border-2 border-gray-200 bg-white/90 backdrop-blur-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-700 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 ease-out font-medium text-base"
                      onClick={handleMicrosoftLogin}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center space-x-3">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Signing in...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-3">
                          <svg className="w-6 h-6" viewBox="0 0 24 24">
                            <path fill="#f25022" d="M1 1h10v10H1z"/>
                            <path fill="#7fba00" d="M13 1h10v10H13z"/>
                            <path fill="#00a4ef" d="M1 13h10v10H1z"/>
                            <path fill="#ffb900" d="M13 13h10v10H13z"/>
                          </svg>
                          <span>Continue with Microsoft</span>
                        </div>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Footer */}
              <div className="text-center mt-8">
                <p className="text-sm text-gray-500 mb-4">
                  © 2025 Printereum. Professional Print Management Platform.
                </p>
                <div className="flex justify-center space-x-6 text-sm">
                  <a href="/privacy" className="text-gray-500 hover:text-gray-700 transition-colors duration-200">Privacy Policy</a>
                  <a href="/terms" className="text-gray-500 hover:text-gray-700 transition-colors duration-200">Terms of Service</a>
                  <a href="/support" className="text-gray-500 hover:text-gray-700 transition-colors duration-200">Support</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
